// import { SessionStatus } from '@qse/auth-ui'
import {
  BuildingOfficeIcon,
  ChartBarIcon,
  CreditCardIcon,
  UserIcon,
} from '@heroicons/react/24/outline'
import { QSE_ENTERPRISE_ID, User, UserRoleEnum } from '@qse/constants'
import { useBillingStatusStore, useUserStore } from '@qse/stores'
import { Navbar, PlusIcon, QuotaAdjustmentBanner, Sidebar, StorageIcon, UploadIcon } from '@qse/ui'
import { getDashboardUrl } from '@qse/utils'
import { type FC, type ReactNode } from 'react'
import { Outlet } from 'react-router-dom'

import { useBillingStatus } from '@/hooks'
import { AppRoutesList } from '@/router/routes'

interface DashboardLayoutProps {
  children?: ReactNode
}

const DashboardLayout: FC<DashboardLayoutProps> = () => {
  const { user } = useUserStore()
  const { showQuotaAdjustmentBanner, quotaAdjustmentGracePeriodStatus } = useBillingStatusStore()

  // Initialize billing status management
  useBillingStatus()

  // Determine the redirect URL using centralized utility
  const getRedirectUrl = () => {
    return getDashboardUrl('/')
  }

  const userType = user?.userRole || ''
  const isTenantAdmin = userType === UserRoleEnum.TENANT_ADMIN
  const isSuperAdmin = userType === UserRoleEnum.SUPER_ADMIN
  const isResellerAdmin = userType === UserRoleEnum.RESELLER_ADMIN

  const shouldShowQuotaAdjustmentBanner =
    isTenantAdmin &&
    showQuotaAdjustmentBanner &&
    !!quotaAdjustmentGracePeriodStatus?.hasActiveGracePeriod &&
    !!quotaAdjustmentGracePeriodStatus?.targetPlan

  const adminMenuItems = [
    ...(isTenantAdmin
      ? [
          {
            id: 'dashboard',
            label: 'Go to Drive',
            icon: PlusIcon,
            isPrimary: true,
            hasArrow: true,
            externalIcon: UploadIcon,
            redirectUrl: getRedirectUrl(),
            isExternal: true,
          },
        ]
      : []),

    ...(isTenantAdmin
      ? [
          {
            id: 'users',
            label: 'Manage Storage',
            icon: StorageIcon,
            redirectUrl: AppRoutesList.users,
          },
          ...(user?.tenant?.resellerId !== QSE_ENTERPRISE_ID
            ? [
                {
                  id: 'subscription',
                  label: 'Manage Subscription',
                  icon: CreditCardIcon,
                  redirectUrl: AppRoutesList.managementSubscription,
                },
              ]
            : []),
        ]
      : []),
    // TODO: show this tabs next release
    ...(isSuperAdmin
      ? [
          {
            id: 'resellers',
            label: 'Reseller Management',
            icon: StorageIcon,
            redirectUrl: AppRoutesList.reseller,
          },
        ]
      : []),
    // TODO: show this tabs next release
    ...(isResellerAdmin || isSuperAdmin
      ? [
          {
            id: 'tenants',
            label: 'Tenant Management',
            icon: BuildingOfficeIcon,
            redirectUrl: AppRoutesList.tenants,
          },
          {
            id: 'plans',
            label: 'Plan Management',
            icon: CreditCardIcon,
            redirectUrl: AppRoutesList.plan,
          },
          {
            id: 'storageReport',
            label: 'Storage Report',
            icon: ChartBarIcon,
            redirectUrl: AppRoutesList.storageReport,
          },
        ]
      : []),

    {
      id: 'profile',
      label: 'Profile',
      icon: UserIcon,
      redirectUrl: AppRoutesList.profile,
    },
  ]

  // Add billing status menu item for tenant admins
  const tenantAdminMenuItems = [
    ...adminMenuItems,
    // ...(isTenantAdmin
    //   ? [
    //       {
    //         id: 'billing-status',
    //         label: 'Billing Status',
    //         icon: CreditCardIcon,
    //         redirectUrl: AppRoutesList.billingStatus,
    //       },
    //     ]
    //   : []),
  ]

  const securitySubmenuItems: Array<{
    label: string
    redirectUrl: string
  }> = []

  // Check if user is logged in
  if (!user) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-black">
      {/* Payment Overdue Banner - only shown to tenant admins */}

      <aside className="bg-white dark:bg-[#282828] text-gray-900 dark:text-white">
        <Sidebar
          isOpen={true}
          menuItems={tenantAdminMenuItems}
          securitySubmenuItems={securitySubmenuItems}
          setIsOpen={() => {}}
          showSecurityTab={false}
          user={user as User}
        />
      </aside>
      {/* Main Content */}
      <main className="flex-1 overflow-y-scroll scrollbar-hide bg-gray-50 dark:bg-black relative">
        <Navbar isTenantAdmin={isTenantAdmin} />
        <Outlet />
        {shouldShowQuotaAdjustmentBanner && <QuotaAdjustmentBanner />}
      </main>
    </div>
  )
}

export default DashboardLayout
